import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Footer from './Footer';

const StaticPage = ({ API, isDarkMode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract page type from URL path
  const getPageType = () => {
    const path = location.pathname;
    if (path.includes('terms')) return 'terms';
    if (path.includes('privacy')) return 'privacy';
    if (path.includes('about')) return 'about';
    return null;
  };

  useEffect(() => {
    loadPage();
  }, [location.pathname]);

  const loadPage = async () => {
    setLoading(true);
    setError(null);
    
    const pageType = getPageType();
    if (!pageType) {
      setError('Invalid page');
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/static-pages/${pageType}`);
      
      if (response.ok) {
        const data = await response.json();
        setPage(data);
      } else if (response.status === 404) {
        setError('Page not found');
      } else {
        setError('Failed to load page');
      }
    } catch (err) {
      console.error('Error loading static page:', err);
      setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`border-b ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Darija Pro
          </h1>
        </div>
      </header>

      {/* Dark mode override for WYSIWYG inline color styles */}
      {isDarkMode && (
        <style>{`
          .static-page-content span[style*="color"],
          .static-page-content font[color],
          .static-page-content p[style*="color"],
          .static-page-content div[style*="color"],
          .static-page-content li[style*="color"],
          .static-page-content h1[style*="color"],
          .static-page-content h2[style*="color"],
          .static-page-content h3[style*="color"] {
            color: inherit !important;
          }
        `}</style>
      )}

      {/* Content */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className={`text-center py-20 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            <p className="text-xl mb-4">😔 {error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Home
            </button>
          </div>
        ) : page ? (
          <article className={`prose prose-lg max-w-none ${
            isDarkMode ? 'prose-invert' : ''
          }`}>
            <div 
              className="static-page-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content) }}
            />
          </article>
        ) : null}
      </main>
    </div>
  );
};

export default StaticPage;
